import useAccess from '@/composables/access/access';
import { aggregatorType, bucketType, chartGroupByOption, gridPageChartType, gridPageType, timeSeriesInterval } from 'shared/constants.json';
import { experimentFlag } from 'shared/experiments.json';
import { useI18n } from 'vue-i18n';

export default function useGridPageConfig() {
  const { t } = useI18n();
  const { accountHasFeature } = useAccess();

  const defaultHeight = 215;

  const getDefaultHeightPerType = (type) => {
    switch (type) {
      case gridPageType.text:
        return 45;
      case gridPageType.goalList:
      case gridPageType.updateFeedList:
      case gridPageType.memberList:
      case gridPageType.checkinList:
      case gridPageType.chart:
      case gridPageType.iframe:
      case gridPageType.media:
        return 400;
      default:
        return defaultHeight;
    }
  };

  const createActions = [
    {
      label: t('gridPage.gridActions.chart'),
      icon: 'PieChartOutlined',
      gridPageType: gridPageType.chart,
      children: [
        {
          key: 'custom',
          label: t('gridPage.gridActions.customChart'),
          icon: 'setting',
          gridPageType: gridPageType.chart,
        },
        {
          key: 'goalProgress',
          label: t('gridPage.gridActions.goalProgressChart'),
          icon: 'line-chart',
          gridPageType: gridPageType.chart,
          tileArgs: {
            title: t('gridPage.gridActions.goalProgressChart'),
            chartTile: {
              chartType: gridPageChartType.line,
              groupBy: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
              aggregationConfig: {
                aggregation: { aggregator: aggregatorType.progressOverTime },
                stackOptions: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
                timeSeriesInterval: timeSeriesInterval.weekly,
              },
            },
          },
        },
        {
          key: 'statusDistribution',
          label: t('gridPage.gridActions.statusDistribution'),
          icon: 'line-chart',
          gridPageType: gridPageType.chart,
          tileArgs: {
            title: t('gridPage.gridActions.statusDistribution'),
            chartTile: {
              chartType: gridPageChartType.line,
              groupBy: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
              aggregationConfig: {
                aggregation: { aggregator: aggregatorType.statusDistributionOverTime },
                stackOptions: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
                timeSeriesInterval: timeSeriesInterval.weekly,
              },
            },
          },
        },
        {
          key: 'statusScore',
          label: t('gridPage.gridActions.statusScore'),
          icon: 'line-chart',
          gridPageType: gridPageType.chart,
          tileArgs: {
            title: t('gridPage.gridActions.statusScore'),
            chartTile: {
              chartType: gridPageChartType.line,
              groupBy: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
              aggregationConfig: {
                aggregation: { aggregator: aggregatorType.statusScoreOverTime },
                stackOptions: { groupBy: chartGroupByOption.none, customBucket: { bucketType: bucketType.automatic } },
                timeSeriesInterval: timeSeriesInterval.weekly,
              },
            },
          },
        },
        {
          key: 'goalsByStatus',
          label: t('gridPage.gridActions.goalsByStatus'),
          icon: 'bar-chart',
          gridPageType: gridPageType.chart,
          tileArgs: { title: t('gridPage.gridActions.goalsByStatus') },
        },
      ],
    },
    { label: t('gridPage.gridActions.singleGoalProgress'), icon: 'compass', gridPageType: gridPageType.singleGoal },
    {
      label: t('gridPage.gridActions.lists'),
      icon: 'UnorderedList',
      gridPageType: 'lists',
      children: [
        { key: 'goalList', label: t('gridPage.gridActions.goalList'), icon: 'compass', gridPageType: gridPageType.goalList },
        { key: 'checkinList', label: t('gridPage.gridActions.checkinList'), icon: 'history', gridPageType: gridPageType.checkinList },
        { key: 'updateFeedList', label: t('gridPage.gridActions.updateFeedList'), icon: 'feedViewIcon', gridPageType: gridPageType.updateFeedList },
        { key: 'memberList', label: t('gridPage.gridActions.memberList'), icon: 'team', gridPageType: gridPageType.memberList },
      ],
    },
    { label: t('gridPage.gridActions.text'), icon: 'alignLeft', gridPageType: gridPageType.text },
    { label: t('gridPage.gridActions.media'), icon: 'fileImage', gridPageType: gridPageType.media },
    { label: t('gridPage.gridActions.iframe'), icon: 'embed-icon', gridPageType: gridPageType.iframe },
  ].filter((action) => action.gridPageType !== gridPageType.chart || accountHasFeature([experimentFlag.customReports]));

  const getMinHeight = (tiles) => {
    const getMinHeightPerTileType = (type) => {
      switch (type) {
        case gridPageType.text:
          return 45;
        default:
          return defaultHeight;
      }
    };

    const minHeights = tiles.map((t) => getMinHeightPerTileType(t.type));
    return Math.max(...minHeights);
  };

  return {
    minColumns: 3,
    maxColumns: 12,
    maxTiles: 4,
    gapWidth: 20,
    defaultHeight,
    getDefaultHeightPerType,
    getMinHeight,
    indicatorHighlightSize: 32,

    createActions,
  };
}
